/* eslint-disable react/prop-types */
import React, { useState, useEffect } from 'react';
import {
  Form,
  Button,
  Row,
  Spin,
  Col,
  Card,
  Input,
  DatePicker,
  Tooltip,
} from 'antd';
import {
  InfoCircleOutlined,
} from '@ant-design/icons';
import {
  capitalize,
} from 'utils/normalizers';
import { getMunicipio, getMunicipios } from 'api/catalogos';
import { ESTADO_PRE_ALTA } from 'utils/constants';
import Select from 'components/Select';
import AvatarUploader from 'components/AvatarUploader';

const HonorarioAsimiladoGeneral = ({
  form,
  rules,
  selectedRowKeys,
  paises,
  estados,
  generos,
  estadoCivil,
  estadosEmpleado,
  nacionalidades,
  motivosAlta,
  calcularEdad,
  disableEstatus,
  isBlocked,
}) => {
  const MEXICANX = 1;
  const EXTRANJERX = 2;
  const PAIS_MEXICO = 152;

  const [loading, setLoading] = useState(false);
  const [municipios, setMunicipios] = useState([]);
  const [estadoRequired, setEstadoRequired] = useState(false);
  const [esNacional, setEsNacional] = useState(true);
  const [paisesList, setPaisesList] = useState(paises);

  const changeNacionalidad = async (nacionalidad) => {
    setLoading(true);
    if (nacionalidad === EXTRANJERX) {
      setEsNacional(false);
      setEstadoRequired(false);
      form.setFieldsValue({
        pais: undefined,
        estado_nacimiento: undefined,
        lugar_de_nacimiento: undefined,
      });
      setPaisesList(paises.filter((pais) => pais.id !== PAIS_MEXICO));
    } else if (nacionalidad === MEXICANX) {
      setEsNacional(true);
      setEstadoRequired(false);
      form.setFieldsValue({
        pais: PAIS_MEXICO,
        estado_nacimiento: undefined,
        lugar_de_nacimiento: undefined,
      });
      setPaisesList(paises);
    }
    setLoading(false);
  };

  const changeState = async (estado) => {
    setLoading(true);
    form.setFieldsValue({
      estado_nacimiento: estado,
      lugar_de_nacimiento: undefined,
    });
    const _municipios = await getMunicipios({ estado });
    setMunicipios(_municipios);
    setLoading(false);
  };

  const cargarCumple = () => {
    const fechaNacimiento = form.getFieldValue('fecha_de_nacimiento');
    const fechaFuturo = (Date.now() - fechaNacimiento);
    if ((fechaNacimiento) && (fechaFuturo > 0)) {
      const edad = calcularEdad(fechaNacimiento);
      form.setFieldsValue({ edad });
    }
  };

  useEffect(() => {
    let mounted = true;
    setEsNacional(form.getFieldValue('nacionalidad') === MEXICANX);

    const fetch = async () => {
      cargarCumple();

      // "Estado" no se persiste: se deriva del Municipio (lugar_de_nacimiento) ya guardado
      // para poder mostrar el filtro correctamente y habilitar el combo de Municipio.
      const lugarDeNacimiento = form.getFieldValue('lugar_de_nacimiento');
      if (lugarDeNacimiento) {
        const municipio = await getMunicipio(lugarDeNacimiento);
        const estadoId = municipio?.estado?.id;
        if (estadoId) {
          const _municipios = await getMunicipios({ estado: estadoId });
          if (mounted) {
            form.setFieldsValue({ estado_nacimiento: estadoId });
            setEstadoRequired(true);
            setMunicipios(_municipios);
          }
          return;
        }
      }

      const _municipios = await getMunicipios();
      if (mounted) {
        setMunicipios(_municipios);
      }
    };
    fetch();
    return () => { mounted = false; };
  }, []);

  useEffect(() => {
    form.setFieldsValue({
      CURP: form.getFieldValue('CURP') || '',
      RFC: form.getFieldValue('RFC') || '',
    });
  }, []);

  const isEditing = selectedRowKeys?.length > 0;

  return (
    <Spin tip="Cargando..." spinning={loading}>
      <Form
        layout="vertical"
        form={form}
        scrollToFirstError
        name="general"
      >
        <Row gutter={10}>
          <Col xs={24} sm={24} md={8}>
            <AvatarUploader
              form={form}
              imageURL={form.getFieldValue('foto')}
            />
          </Col>

          <Col xs={24} sm={24} md={8}>
            <Tooltip
              title={
                isEditing
                  ? '¡Importante! Cambiar el No. de Honorario Asimilado puede afectar referencias en nómina, historial, reportes, etc.'
                  : ''
              }
              placement="top"
            >
              <Form.Item
                name="numero_de_honorario_asimilado"
                rules={rules.clave}
                label={(
                  <>
                    No. Honorario Asimilado
                    {isEditing && (
                      <InfoCircleOutlined
                        style={{ marginLeft: 6, color: '#faad14' }}
                      />
                    )}
                  </>
                )}
              >
                <Input
                  allowClear
                  disabled={isBlocked && selectedRowKeys.length === 0}
                />
              </Form.Item>
            </Tooltip>
          </Col>

          <Col xs={24} sm={24} md={8}>
            <Form.Item
              name="numero_de_honorario_asimilado_anterior"
              label="No. Honorario Asimilado Anterior"
            >
              <Input
                allowClear
                disabled={isBlocked}
              />
            </Form.Item>
          </Col>
        </Row>
        <Card
          type="inner"
          size="small"
          className="inner-block-card"
          title="1. Nombre Completo"
          extra="Tal Como Aparece En Documentos Oficiales"
        >
          <Row gutter={10}>
            <Col xs={24} sm={24} md={8}>
              <Form.Item
                name="nombre"
                label="Nombre"
                rules={rules.nombre}
                hasFeedback
                normalize={capitalize}
              >
                <Input
                  allowClear
                  disabled={isBlocked}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={24} md={8}>
              <Form.Item
                name="apellido_paterno"
                label="Apellido Paterno"
                rules={rules.solo_letras_requerido}
                hasFeedback
                normalize={capitalize}
              >
                <Input
                  allowClear
                  disabled={isBlocked}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={24} md={8}>
              <Form.Item
                name="apellido_materno"
                label="Apellido Materno"
                rules={rules.solo_letras}
                hasFeedback
                normalize={capitalize}
              >
                <Input
                  allowClear
                  disabled={isBlocked}
                />
              </Form.Item>
            </Col>
          </Row>
        </Card>
        <Card
          type="inner"
          size="small"
          className="inner-block-card"
          title="2. Datos Personales"
          extra="Género, Nacionalidad, Nacimiento Y Estado Civil"
        >
          <Row gutter={10}>
            <Col xs={24} sm={24} md={8}>
              <Form.Item
                name="genero"
                label="Género"
                rules={rules.required}
                hasFeedback
              >
                <Select
                  dataSource={generos}
                  disabled={isBlocked}
                />
              </Form.Item>
            </Col>

            <Col xs={24} sm={24} md={8}>
              <Form.Item
                name="nacionalidad"
                label="Nacionalidad"
                rules={rules.required}
                hasFeedback
              >
                <Select
                  dataSource={nacionalidades}
                  onChange={changeNacionalidad}
                  disabled={isBlocked}
                />
              </Form.Item>
            </Col>

            <Col xs={24} sm={24} md={8}>
              <Form.Item
                name="pais"
                label="País"
                rules={rules.required}
                hasFeedback
              >
                <Select
                  dataSource={paisesList}
                  labelProp="nombre"
                  disabled={esNacional || !form.getFieldValue('nacionalidad') || isBlocked}
                />
              </Form.Item>
            </Col>

            <Col xs={24} sm={24} md={8}>
              <Form.Item
                name="fecha_de_nacimiento"
                label="Fecha de Nacimiento"
                rules={rules.fecha_de_nacimiento}
                hasFeedback
              >
                <DatePicker
                  placeholder=""
                  format="DD/MM/YYYY"
                  onChange={(value) => {
                    form.setFieldsValue({
                      edad: calcularEdad(value),
                    });
                  }}
                  disabled={isBlocked}
                />
              </Form.Item>
            </Col>

            <Col xs={24} sm={24} md={8}>
              <Form.Item
                name="estado_nacimiento"
                label="Estado"
                hasFeedback
                normalize={capitalize}
              >
                <Select
                  dataSource={estados}
                  labelProp="nombre_de_AGEE"
                  disabled={!esNacional || isBlocked}
                  onChange={(value) => {
                    if (value > 0) {
                      setEstadoRequired(true);
                      changeState(value);
                    } else if (value === undefined) {
                      setEstadoRequired(false);
                      form.resetFields(['lugar_de_nacimiento']);
                    }
                  }}
                />
              </Form.Item>
            </Col>

            <Col xs={24} sm={24} md={8}>
              <Form.Item
                name="edad"
                label="Edad"
              >
                <Input disabled />
              </Form.Item>
            </Col>

            <Col xs={24} sm={24} md={8}>
              <Form.Item
                name="lugar_de_nacimiento"
                label="Municipio"
                hasFeedback
                rules={estadoRequired ? rules.required : null}
              >
                <Select
                  dataSource={municipios}
                  labelProp="nom_mun"
                  disabled={!estadoRequired || !esNacional || isBlocked}
                />
              </Form.Item>
            </Col>

            <Col xs={24} sm={24} md={8}>
              <Form.Item
                name="estado_civil"
                label="Estado Civil"
                rules={rules.required}
                hasFeedback
              >
                <Select
                  dataSource={estadoCivil}
                  disabled={isBlocked}
                />
              </Form.Item>
            </Col>

            <Col xs={24} sm={24} md={8}>
              <Form.Item
                name="tipo_sangre"
                label="Tipo de Sangre"
                rules={rules.required}
                hasFeedback
              >
                <Input
                  allowClear
                  disabled={isBlocked}
                />
              </Form.Item>
            </Col>
          </Row>
        </Card>
        <Card
          type="inner"
          size="small"
          className="inner-block-card"
          title="3. Estatus Y Movimiento"
          extra="Estado Actual En Nómina"
        >
          <Row gutter={10}>
            {selectedRowKeys.length ? (
              <Col xs={24} sm={24} md={8}>
                <Form.Item
                  name="estado_del_honorario_asimilado"
                  label="Estatus del Honorario Asimilado"
                  rules={rules.required}
                  hasFeedback
                >
                  <Select
                    activeInactiveEmpleado={!disableEstatus}
                    dataSource={estadosEmpleado}
                    disabled={disableEstatus || isBlocked}
                  />
                </Form.Item>
              </Col>
            ) : (
              <>
                <Col xs={24} sm={24} md={8}>
                  <Form.Item
                    name="estado_del_honorario_asimilado"
                    label="Estatus del Honorario Asimilado"
                    rules={rules.required}
                    hasFeedback
                    initialValue={ESTADO_PRE_ALTA}
                    disabled
                  >
                    <Select dataSource={estadosEmpleado} disabled />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={24} md={8}>
                  <Form.Item
                    name="fecha_de_alta_inicial"
                    label="Fecha de Alta"
                    hasFeedback
                  >
                    <DatePicker
                      placeholder=""
                      format="DD/MM/YYYY"
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={24} md={8}>
                  <Form.Item
                    name="motivo_alta_inicial"
                    label="Motivo de Alta"
                    hasFeedback
                  >
                    <Select
                      dataSource={motivosAlta}
                      render={(value) => `${value?.concepto}`}
                    />
                  </Form.Item>
                </Col>
              </>
            )}
          </Row>
        </Card>

        <Form.Item hidden>
          <Button htmlType="submit" />
        </Form.Item>
      </Form>
    </Spin>
  );
};

export default HonorarioAsimiladoGeneral;
